import { forwardRef, useEffect, useId, useRef, type InputHTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

import { CheckIcon } from '../internal/icons';
import { joinIds } from '../internal/aria';
import styles from './Checkbox.module.css';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  hint?: ReactNode;
  /** Partially-selected parent in a tree of choices. Sets the DOM property. */
  indeterminate?: boolean;
}

/**
 * The input stays the visible box — it is never hidden and its outline is never
 * removed, so the focus ring comes free from the token stylesheet. The glyph is
 * an overlay because pseudo-elements do not render on void elements.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, indeterminate = false, className, ...rest },
  forwardedRef,
) {
  const generated = useId();
  const id = rest.id ?? `${generated}-checkbox`;
  const hintId = `${generated}-hint`;
  const innerRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    // `indeterminate` exists only as a DOM property; React has no attribute for
    // it, so it has to be written after render.
    if (innerRef.current !== null) innerRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <div className={clsx(styles.root, className)}>
      <span className={styles.control}>
        <input
          {...rest}
          ref={(node) => {
            innerRef.current = node;
            if (typeof forwardedRef === 'function') forwardedRef(node);
            else if (forwardedRef !== null) forwardedRef.current = node;
          }}
          id={id}
          type="checkbox"
          className={styles.input}
          aria-describedby={joinIds(rest['aria-describedby'], hint !== undefined && hintId)}
        />
        <span className={styles.indicator} aria-hidden="true">
          <span className={styles.check}>
            <CheckIcon />
          </span>
          <span className={styles.dash} />
        </span>
      </span>
      <span className={styles.text}>
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
        {hint !== undefined && (
          <span className={styles.hint} id={hintId}>
            {hint}
          </span>
        )}
      </span>
    </div>
  );
});
